import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { Icon } from '@/components/ui';
// Submodule path: the backoffice barrel evaluates every backoffice screen.
import {
  backofficeAreasForProfile,
  backofficeShortcutsForProfile,
  type BackofficeArea,
} from '@/features/backoffice/sections';
import { useCurrentUserName } from '@/features/organization/queries';
import { THEME_LABELS, THEME_PREFERENCES, useThemePreference } from '@/lib/theme';

import { NavIcon } from '../NavIcon';
import { navigationLayoutForProfile, PROFILE_LABELS } from '../navigation';
import { SignOutDialog } from '../SignOutDialog';
import { useCommandPaletteShortcut } from '../useCommandPaletteShortcut';
import storeStyles from '../store/StoreHeader.module.css';
import menuStyles from '../store/StoreAccountMenu.module.css';
import styles from './InternalHeader.module.css';

const normalise = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/gu, '').toLowerCase();

function useOutside(open: boolean, close: () => void, ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, ref]);
}

/** "Todas as áreas" (Figma 94:1972): every section the profile reaches, in the redesign's groups. */
function AllAreas({ areas }: { areas: readonly BackofficeArea[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutside(open, close, ref);
  return (
    <div className={storeStyles.categories} ref={ref}>
      <button
        type="button"
        className={styles.areasButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Icon name="menu" size={18} strokeWidth={1.6} />
        Todas as áreas
        <Icon name="chevronDown" size={14} strokeWidth={2} />
      </button>
      {open ? (
        <div className={styles.areasMenu} role="menu" aria-label="Todas as áreas">
          {areas.map((area) => (
            <div key={area.label} className={styles.area}>
              <p className={styles.areaTitle}>{area.label}</p>
              {area.sections.map((section) => (
                <Link
                  key={section.to}
                  to={section.to}
                  role="menuitem"
                  className={styles.areaLink}
                  viewTransition
                  onClick={close}
                >
                  <NavIcon name={section.to} />
                  {section.label}
                </Link>
              ))}
            </div>
          ))}
          <p className={styles.areasHint}>⌘K busca qualquer área de qualquer tela</p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The internal search (Figma BC1 "Buscar pedidos, clientes ou produtos"): today it finds the
 * profile's own areas and pages by name, as ⌘K always did. Searching orders, customers and
 * products from here needs a cross-entity search the API does not have; the PR lists it.
 */
function InternalSearch({ areas }: { areas: readonly BackofficeArea[] }) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutside(open, close, ref);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useCommandPaletteShortcut(
    useCallback(() => {
      inputRef.current?.focus();
    }, []),
  );

  const options = useMemo(() => {
    const words = normalise(value).split(/\s+/u).filter(Boolean);
    return areas
      .flatMap((area) => area.sections.map((section) => ({ ...section, area: area.label })))
      .filter((option) =>
        words.every((word) => normalise(`${option.label} ${option.area}`).includes(word)),
      )
      .slice(0, 8);
  }, [areas, value]);

  const go = (to: string) => {
    setOpen(false);
    setValue('');
    inputRef.current?.blur();
    void navigate(to, { viewTransition: true });
  };

  return (
    <div className={styles.search} ref={ref} data-open={open ? '' : undefined}>
      <div className={styles.searchField}>
        <span className={styles.scope}>Áreas</span>
        <Icon name="search" size={20} strokeWidth={1.5} />
        <input
          ref={inputRef}
          type="search"
          className={styles.searchInput}
          placeholder="Buscar uma área ou página (ex.: tabelas de preço, entregas)"
          aria-label="Buscar uma área ou página"
          role="combobox"
          aria-expanded={open}
          aria-controls="busca-areas"
          aria-autocomplete="list"
          autoComplete="off"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const step = event.key === 'ArrowDown' ? 1 : -1;
              setActive(
                (current) => (current + step + options.length) % Math.max(options.length, 1),
              );
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const option = options[active];
              if (option !== undefined) go(option.to);
            } else if (event.key === 'Escape') {
              close();
              inputRef.current?.blur();
            }
          }}
        />
        <kbd className={styles.kbd} aria-hidden="true">
          ⌘K
        </kbd>
      </div>
      {open && options.length > 0 ? (
        <ul id="busca-areas" className={styles.results} role="listbox" aria-label="Áreas">
          {options.map((option, index) => (
            <li
              key={option.to}
              role="option"
              aria-selected={index === active}
              data-active={index === active ? '' : undefined}
              className={styles.result}
              onMouseEnter={() => {
                setActive(index);
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                go(option.to);
              }}
            >
              <NavIcon name={option.to} />
              <span>{option.label}</span>
              <span className={styles.resultArea}>{option.area}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function initialsOf(name: string): string {
  const words = name.split(/[\s@.]+/u).filter(Boolean);
  return `${words[0]?.charAt(0) ?? ''}${words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : ''}`.toUpperCase();
}

/** "Olá, Bruna · Conta e acessos" — the internal account menu (Figma BC1, AD11). */
function InternalAccount({ onHelp }: { onHelp: () => void }) {
  const { user, profile } = useAuth();
  const fullName = useCurrentUserName();
  const [theme, setTheme] = useThemePreference();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutside(open, close, ref);
  const email = user?.email ?? 'Sessão anônima';
  const name = fullName ?? email;
  const profileLabel = profile === null ? 'Sem perfil' : PROFILE_LABELS[profile];
  const pages = navigationLayoutForProfile(profile).menu;

  return (
    <div className={menuStyles.root} ref={ref}>
      <button
        type="button"
        className={menuStyles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Conta: ${name}, ${profileLabel}`}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <span className={menuStyles.avatar} aria-hidden="true">
          {initialsOf(fullName ?? email)}
        </span>
        <span className={menuStyles.triggerText} aria-hidden="true">
          <span className={menuStyles.triggerHello}>
            {fullName !== undefined ? `Olá, ${fullName.split(/\s+/u)[0] ?? ''}` : 'Olá'}
          </span>
          <span className={menuStyles.triggerTitle}>
            Conta e acessos
            <Icon name="chevronDown" size={14} strokeWidth={2} />
          </span>
        </span>
      </button>
      {open
        ? createPortal(<div className={menuStyles.veil} aria-hidden="true" />, document.body)
        : null}
      {open ? (
        <div className={menuStyles.menu} role="menu" aria-label="Conta e acessos">
          <div className={menuStyles.block}>
            <div className={menuStyles.who}>
              <span className={menuStyles.avatarLarge} aria-hidden="true">
                {initialsOf(fullName ?? email)}
              </span>
              <div>
                <p className={menuStyles.whoName}>{name}</p>
                {fullName !== undefined ? <p className={menuStyles.whoEmail}>{email}</p> : null}
                <p className={menuStyles.whoProfile}>{profileLabel}</p>
              </div>
            </div>
          </div>
          {pages.length > 0 ? (
            <div className={menuStyles.block}>
              {pages.map((page, index) => (
                <NavLink
                  key={page.to}
                  to={page.to}
                  role="menuitem"
                  className={menuStyles.item ?? ''}
                  style={{ '--i': index } as React.CSSProperties}
                  onClick={close}
                  viewTransition
                >
                  <NavIcon name={page.to} />
                  <span className={menuStyles.itemLabel}>{page.label}</span>
                </NavLink>
              ))}
            </div>
          ) : null}
          <div className={menuStyles.block}>
            <div className={menuStyles.themeRow} role="group" aria-label="Aparência">
              <span className={menuStyles.themeLabel} aria-hidden="true">
                Aparência
              </span>
              <div className={menuStyles.themeOptions}>
                {THEME_PREFERENCES.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="menuitemradio"
                    aria-checked={theme === option}
                    className={menuStyles.themeOption}
                    onClick={() => {
                      setTheme(option);
                    }}
                  >
                    {THEME_LABELS[option]}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={menuStyles.block}>
            <button
              type="button"
              role="menuitem"
              className={menuStyles.item}
              onClick={() => {
                close();
                onHelp();
              }}
            >
              <Icon name="help" size={18} className={menuStyles.itemIcon} />
              <span className={menuStyles.itemLabel}>Ajuda e atalhos</span>
              <kbd className={menuStyles.kbd}>⌘K</kbd>
            </button>
            <button
              type="button"
              role="menuitem"
              className={menuStyles.item}
              onClick={() => {
                close();
                setSigningOut(true);
              }}
            >
              <Icon name="logout" size={18} className={menuStyles.itemIcon} />
              <span className={menuStyles.itemLabel}>Sair</span>
            </button>
          </div>
        </div>
      ) : null}
      <SignOutDialog
        open={signingOut}
        onClose={() => {
          setSigningOut(false);
        }}
      />
    </div>
  );
}

/** The phone's drawer (Figma BC3M): the profile's areas, grouped, from the menu button. */
function InternalDrawer({
  open,
  onClose,
  areas,
}: {
  open: boolean;
  onClose: () => void;
  areas: readonly BackofficeArea[];
}) {
  const { profile } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    onClose();
    // Closing on navigation only: `onClose` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  if (!open) return null;
  return createPortal(
    <div className={menuStyles.drawerLayer}>
      <button
        type="button"
        className={menuStyles.drawerVeil}
        aria-label="Fechar o menu"
        onClick={onClose}
      />
      <nav className={menuStyles.drawer} aria-label="Menu">
        <div className={menuStyles.drawerHead}>
          <div className={menuStyles.drawerWho}>
            <p className={menuStyles.drawerName}>Backoffice</p>
            <p className={menuStyles.drawerProfile}>
              {profile === null ? '' : PROFILE_LABELS[profile]}
            </p>
          </div>
          <button
            type="button"
            className={menuStyles.drawerClose}
            onClick={onClose}
            aria-label="Fechar"
          >
            <Icon name="close" size={22} />
          </button>
        </div>
        {areas.map((area) => (
          <div key={area.label}>
            <p className={menuStyles.drawerGroup}>{area.label}</p>
            {area.sections.map((section, index) => (
              <NavLink
                key={section.to}
                to={section.to}
                className={({ isActive }) =>
                  [menuStyles.item, isActive ? menuStyles.itemActive : null]
                    .filter(Boolean)
                    .join(' ')
                }
                style={{ '--i': index } as React.CSSProperties}
                viewTransition
              >
                <NavIcon name={section.to} />
                <span className={menuStyles.itemLabel}>{section.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </div>,
    document.body,
  );
}

export interface InternalHeaderProps {
  readonly onHelp: () => void;
  /** "5 pedidos para confirmar"-style pill, when the profile's home has one. */
  readonly pill?: { readonly label: string; readonly to: string } | undefined;
}

/**
 * The internal profiles' header (Figma "Esboço · perfis internos", BC1/CP1/CT1/AD1): the same red
 * band as the storefront, with "Backoffice" beside the brand, the search, "Operando como" and the
 * account; a second line with "Todas as áreas" (the sections, grouped per profile) and the
 * profile's daily shortcuts. It replaces the top bar's page switcher and the backoffice's own
 * sidebar and tabs.
 */
export function InternalHeader({ onHelp, pill }: InternalHeaderProps) {
  const { profile } = useAuth();
  const shortcuts = useMemo(() => backofficeShortcutsForProfile(profile), [profile]);
  // The tenant-side pages an internal profile also reaches (the catalogue, "Pedidos" as a
  // customer sees them) close "Todas as áreas", under their own heading.
  const areas = useMemo(() => {
    const own = backofficeAreasForProfile(profile);
    const outside = navigationLayoutForProfile(profile)
      .primary.filter((item) => item.to !== '/backoffice')
      .map((item) => ({
        to: item.to,
        label: item.label,
        group: 'overview' as const,
        profiles: [],
      }));
    return outside.length > 0 ? [...own, { label: 'Como o cliente vê', sections: outside }] : own;
  }, [profile]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  return (
    <header className={storeStyles.header} data-topbar="" data-internal-header="">
      <div className={storeStyles.main}>
        <button
          type="button"
          className={storeStyles.menuButton}
          aria-label="Abrir o menu"
          aria-expanded={drawerOpen}
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          <Icon name="menu" size={24} />
        </button>
        {/* The internal home: "/" lands each profile on its first module, which for Compras is the
            catalogue (open question 7 of the Figma note); the brand leads to the backoffice's. */}
        <Link to="/backoffice" className={storeStyles.brand} viewTransition>
          <span className={storeStyles.mark} aria-hidden="true">
            In
          </span>
          Instoque
          <span className={styles.chip}>Backoffice</span>
        </Link>
        <div className={storeStyles.searchBar}>
          <InternalSearch areas={areas} />
        </div>
        {profile !== null ? (
          <p className={storeStyles.unit}>
            <Icon name="users" size={22} strokeWidth={1.65} />
            <span className={storeStyles.unitText}>
              <span className={storeStyles.unitLabel}>Operando como</span>
              <span className={storeStyles.unitName}>{PROFILE_LABELS[profile]}</span>
            </span>
          </p>
        ) : null}
        <div className={storeStyles.tools}>
          <div className={storeStyles.account}>
            <InternalAccount onHelp={onHelp} />
          </div>
        </div>
      </div>

      <nav className={storeStyles.shortcuts} aria-label="Atalhos do perfil">
        {areas.length > 0 ? <AllAreas areas={areas} /> : null}
        {shortcuts.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            className={storeStyles.shortcut ?? ''}
            viewTransition
          >
            {section.label}
          </NavLink>
        ))}
        <span className={storeStyles.spacer} />
        {pill !== undefined ? (
          <Link to={pill.to} className={storeStyles.openPill} viewTransition>
            <Icon name="clock" size={16} strokeWidth={1.4} />
            {pill.label}
          </Link>
        ) : null}
        <button type="button" className={storeStyles.help} onClick={onHelp}>
          <Icon name="help" size={16} strokeWidth={1.4} />
          Ajuda
        </button>
      </nav>

      <InternalDrawer open={drawerOpen} onClose={closeDrawer} areas={areas} />
    </header>
  );
}
